import React, { useState, useEffect } from "react";
import { Container, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Add as AddIcon } from "@mui/icons-material";

import NavigationHeader from "../../../components/NavigationHeader";
import { Loading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";

import ModuleFilters from "../components/ModuleFilters";
import ProjectModulesSkeleton from "../components/skeletons/ProjectModulesSkeleton";

import { ProjectFilter, SuperProject as SuperProjectType } from "../types";
import { projectModuleApi, superProjectApi } from "../services/apiService";
import { PROJECT_TERMINOLOGY } from "../enums";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import { UserScope } from "../../../types/operationType";
import SuperProjectList from "../components/SuperProjectList";

const SuperProject: React.FC = () => {
  const navigate = useNavigate();

  const { planStatus, loading: planStatusLoading } = usePlanStatus();

  const [superProject, setSuperProject] = useState<SuperProjectType[]>([]);
  const [filters, setFilters] = useState<ProjectFilter>({});
  const [isLoading, setIsLoading] = useState(true);

  // Modal states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<string | null>(null);

  const { showToast } = useToast();

  // Fetch superProject
  const fetchSuperProjects = async () => {
    setIsLoading(true);
    try {
      let data;
      data = await superProjectApi.getSuperProjects(filters);
      setSuperProject(data);
    } catch (error) {
      showToast("Something went wrong", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    if (!planStatusLoading) {
      fetchSuperProjects();
    }
  }, [filters, planStatusLoading]);

  // Handlers
  const handleAddModule = () => {
    navigate(`/super/form`);
  };

  const handleEditModule = (module: SuperProjectType) => {
    navigate(`/super/form/${module.id}`);
  };

  const handleViewModule = (module: SuperProjectType) => {
    navigate(`/super/${module.id}`);
  };

  const handleDeleteModule = (id: string) => {
    setModuleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!moduleToDelete) return;

    try {
      await projectModuleApi.deleteModule(moduleToDelete);
      showToast(
        `${PROJECT_TERMINOLOGY.SINGULAR} deleted successfully`,
        "success"
      );
      fetchSuperProjects();
    } catch (error) {
      showToast(
        `Failed to delete ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}`,
        "error"
      );
    } finally {
      setDeleteDialogOpen(false);
      setModuleToDelete(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{py: 4}}>
      <NavigationHeader
        title={`${PROJECT_TERMINOLOGY.PLURAL}`}
        button={
          planStatus.user?.userRole?.scope === UserScope.GLOBAL
            ? {
                label: `Add ${PROJECT_TERMINOLOGY.SINGULAR}`,
                onClick: handleAddModule,
                icon: <AddIcon />,
              }
            : null
        }
        backNavigationEnabled={false}
      />

      {/* Filters */}
      <ModuleFilters filters={filters} onFiltersChange={setFilters} />

      {/* List View */}
      <Box sx={{ position: "relative" }}>
        {isLoading && superProject.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
            }}
          >
            <Loading
              message={`Updating ${PROJECT_TERMINOLOGY.LOWER_PLURAL}...`}
              fullScreen={false}
            />
          </Box>
        )}

        {isLoading && superProject.length === 0 ? (
          <ProjectModulesSkeleton />
        ) : (
          <SuperProjectList
            superProjects={superProject}
            onView={handleViewModule}
            onEdit={handleEditModule}
            onDelete={handleDeleteModule}
            userScope={planStatus.user?.userRole?.scope}
          />
        )}
      </Box>

      <DeleteConfirmationDialog
        deleteModalOpen={deleteDialogOpen}
        handleNo={() => setDeleteDialogOpen(false)}
        handleYes={handleConfirmDelete}
        itemToDelete={moduleToDelete}
        title={`Delete ${PROJECT_TERMINOLOGY.SINGULAR}`}
        message={`Are you sure you want to delete this ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}? This action cannot be undone.`}
      />
    </Container>
  );
};

export default SuperProject;
